package modysy.tools

import modysy.GameController
import modysy.datastructures.Vector2
import modysy.model.{GameObject, Portal, Transform}
import modysy.view.{Color, ImageProvider}
import org.scalajs.dom
import org.scalajs.dom.CanvasRenderingContext2D

/**
  * Implements a tool for placing portals with the given lifetime
  * into the game world controlled by the given GameController.
  *
  * @param gameController the active game controller
  * @param portalLifetime the lifetime of the obstacle
  */
class PortalPlacementTool(override protected val gameController: GameController,
) extends PlacementTool {

  override protected var currentOrientation: Double = 0

  /** A dummy object used to check for collisions with objects already
    * present in the level. */
  override protected def placedObject: GameObject = new Portal(true)

  // previous rotation angle while rotating the obstacle
  private var touchRotatePreviousAngle: Option[Double] = None

  // if the tool is touched
  private var touchOnTool: Boolean = false

  private var entranceSet: Boolean = false
  private var entrancePortal: Portal = null

  /** Renders the icon of this tool onto the given rendering context.
    * The icon is drawn with its center at (0, 0) so the context
    * need to be translated correctly.
    *
    * @param ctx context to render the icon on
    */
  override def renderIcon(ctx: CanvasRenderingContext2D): Unit = {
    ctx.save()
    ctx.scale(0.4, 0.4)
    ImageProvider
      .drawImgFnc(ImageProvider.getImage("portal-in"), 100, 100)
      .apply(ctx, -50, -50)
    ctx.restore()
    //TODO: Check if those values are correct
  }

  /** Renders the non-icon parts of this tool at the tools position set
    * by moveTo(...), moveBy(...) or even moveReset(...).
    * This may render nothing at all (e.g. is tool is not inside the game world).
    *
    * @param ctx context to render on
    */
  override def render(ctx: dom.CanvasRenderingContext2D): Unit = {
    require(_position.isDefined)

    ctx.save()
    ctx.globalAlpha = 0.5

    ctx.translate(_position.get.x, _position.get.y)
    ctx.rotate(currentOrientation)

    if (isPlaceable(_position.get)) {
      ImageProvider
        .drawImgFnc(ImageProvider.getImage("portal-in"), 100, 100) //TODO: Add right image
        .apply(ctx, -50, -50)
    } else {
      ctx.fillStyle = Color.Red
      ctx.fillRect(-50, -50, 100, 100)
      ImageProvider
        .drawImgFnc(ImageProvider.getImage("portal-in"), 100, 100) //TODO: add right image
        .apply(ctx, -50, -50)
    }

    ctx.restore()
    //TODO: I have literally no idea what that does
  }

  /** Uses this tool at the tools position set by moveTo(...),
    * moveBy(...) or even moveReset(...).
    *
    * Returns true if the tool was used successfully, otherwise false.
    *
    * @return Whether the interaction was successful.
    */
  override def use(): Boolean = {
    require(_position.isDefined)

    if (isPlaceable(_position.get)) {

      if (entranceSet) {
        val gameObject: Portal = new Portal(false)
        gameObject.body.pose = Transform
          .translate(_position.get)
          .rotate(currentOrientation)

        gameController.getLevel.portals ++= Seq((entrancePortal, gameObject))
        entranceSet = false
      } else {
        entrancePortal = new Portal(true)
        entrancePortal.body.pose = Transform
          .translate(_position.get)
          .rotate(currentOrientation)

        entranceSet = true
      }

      true
      //TODO: Add here that two objects have to be set
    } else false
  }

  /** Returns the non-negative amount of energy points needed to
    * use this tool once. This amount of points will be spent
    * using this tool once.
    *
    * @return energy cost to use this tool once
    */
  override def cost: Int = 0

  /** Call this when the user starts to touch and touches the canvas at two or less spots simultaneously.
    *
    * If the tool is touched, then a second touch somewhere else will enable obstacle rotation.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchWorldPos world position of the starting touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchStart(touchWorldPos: Vector2): Boolean = {
    if (isOver(touchWorldPos)) { // touch to the tool
      touchOnTool = true
    }

    if (touchOnTool) { // tool already touched and second touch somewhere else
      require(_position.isDefined, "TouchOnTool but tool not positioned")

      val pos = _position.get // tools position
      val diff = (touchWorldPos - pos).unit // normalized vector from tool to touch pos

      touchRotatePreviousAngle = Some(Vector2(0, 1).dot(diff))

      return true
    }

    false
  }

  private def handleRotate(toolToTouch: Vector2): Unit = {
    val rotateCurrentAngle = Vector2(0, 1).dot(toolToTouch)

    currentOrientation += touchRotatePreviousAngle.get - rotateCurrentAngle
    touchRotatePreviousAngle = Some(rotateCurrentAngle)
  }

  /** Call this when the user moves his touch and touches to the canvas at exactly two spots simultaneously.
    *
    * If obstacle rotation was enabled via the gesture, then moving the finger not on the tool
    * will rotate the obstacle.
    *
    * Moving the finger touching the tool away will disable obstacle rotation.
    *
    * Returns true if the touch gesture has rotated the obstacle and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touch1WorldPos world position of the first touch relative to the upper left corner of the world
    * @param touch2WorldPos world position of the second touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchMove(touch1WorldPos: Vector2, touch2WorldPos: Vector2): Boolean = {
    if (touchRotatePreviousAngle.isDefined) {
      require(_position.isDefined,
              "touchRotatePreviousAngle defined but tool not positioned")

      val pos = _position.get // tools position

      if (isOver(touch1WorldPos) && !isOver(touch2WorldPos)) {
        val diff = (touch2WorldPos - pos).unit // normalized vector from tool to touch pos
        handleRotate(diff)

        return true
      }

      if (isOver(touch2WorldPos) && !isOver(touch1WorldPos)) {
        val diff = (touch1WorldPos - pos).unit // normalized vector from tool to touch pos
        handleRotate(diff)

        return true
      }

      // moved away from the tool
      touchRotatePreviousAngle = None
      touchOnTool = false
    }

    false
  }

  /** Call this when the user ends all or all but one of his touches to the canvas.
    *
    * If obstacle rotation is enabled but the tool is not touched any longer, then it gets disabled.
    *
    * Always returns false indicating that this gesture may trigger other actions (e.g. scrolling).
    *
    * @param remainingTouchWorldPos world position of the remaining touch relative to the upper left corner of the world
    *                               or None
    * @return false, indicating that his gesture is not consumed
    */
  def onTouchEnd(remainingTouchWorldPos: Option[Vector2]): Boolean = {
    touchRotatePreviousAngle = None

    if (remainingTouchWorldPos.isDefined) {
      if (!isOver(remainingTouchWorldPos.get)) {
        touchOnTool = false
      }
    } else {
      touchOnTool = false
    }

    false
  }

  def rotateCW(): Unit = {
    currentOrientation += 2.0 / 180.0 * Math.PI
  }

  def rotateCCW(): Unit = {
    currentOrientation -= 2.0 / 180.0 * Math.PI
  }

  override var uses: Int = _
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/PortalPlacementTool.scala